/**
 * PWA Install Prompt Handler
 * Shows custom install banner after user engagement
 * Different messaging based on user role
 */

import { useEffect, useState, useCallback } from 'react';

// Storage keys
const INSTALL_DISMISSED_KEY = 'pwa_install_dismissed';
const INSTALL_DISMISSED_UNTIL_KEY = 'pwa_install_dismissed_until';
const PAGE_VISIT_COUNT_KEY = 'pwa_page_visits';

// Days to wait after dismissal before showing again
const DISMISS_DAYS = 7;

// Pages to visit before showing prompt
const PAGES_BEFORE_PROMPT = 3;

/**
 * Get role-specific install message
 */
export function getInstallMessage(role) {
  switch (role) {
    case 'admin':
      return {
        title: 'Install TheScope180',
        message: 'Manage your platform on the go — Install TheScope180 for quick access to admin tools.',
        icon: '🛠️'
      };
    case 'specialist':
    case 'clientSuccess':
      return {
        title: 'Install TheScope180',
        message: 'Respond to chats faster — Install TheScope180 for instant notifications.',
        icon: '💬'
      };
    case 'mentor':
      return {
        title: 'Install TheScope180',
        message: 'Stay connected with your mentees — Install TheScope180 for easy access.',
        icon: '🎓'
      };
    default:
      return {
        title: 'Install TheScope180',
        message: 'Access your courses anytime, anywhere — Install TheScope180 for the best learning experience.',
        icon: '📚'
      };
  }
}

/**
 * Check if we should show the install prompt
 */
export function shouldShowInstallPrompt() {
  // Check if already installed
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return false;
  }

  // Check if on iOS (needs manual instructions)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS && !('beforeinstallprompt' in window)) {
    // iOS doesn't support beforeinstallprompt, but we can still show manual instructions
    // We'll handle this separately
  }

  // Check dismissal
  const dismissedUntil = localStorage.getItem(INSTALL_DISMISSED_UNTIL_KEY);
  if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
    return false;
  }

  // Check page visit count
  const visitCount = parseInt(localStorage.getItem(PAGE_VISIT_COUNT_KEY) || '0', 10);
  if (visitCount < PAGES_BEFORE_PROMPT) {
    return false;
  }

  return true;
}

/**
 * Increment page visit counter
 */
export function trackPageVisit() {
  const currentCount = parseInt(localStorage.getItem(PAGE_VISIT_COUNT_KEY) || '0', 10);
  localStorage.setItem(PAGE_VISIT_COUNT_KEY, String(currentCount + 1));
}

/**
 * Dismiss the install prompt
 */
export function dismissInstallPrompt(remindLater = true) {
  if (remindLater) {
    // Set reminder for 7 days from now
    const dismissUntil = Date.now() + (DISMISS_DAYS * 24 * 60 * 60 * 1000);
    localStorage.setItem(INSTALL_DISMISSED_UNTIL_KEY, String(dismissUntil));
  } else {
    // Permanent dismiss (until cache clear)
    localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
  }
}

/**
 * Check if device is iOS
 */
export function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Check if app is already installed
 */
export function isAppInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

/**
 * Custom hook for PWA install prompt
 */
export function useInstallPrompt(userRole = 'learner') {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    setIsInstalled(isAppInstalled());

    // Check if iOS
    setIsIOS(isIOSDevice());

    // Track page visit
    trackPageVisit();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Check if we should show our custom prompt
      if (shouldShowInstallPrompt()) {
        // Small delay to not interrupt user immediately
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      console.log('[PWA] App was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if we should show iOS instructions
    if (isIOSDevice() && !isAppInstalled() && shouldShowInstallPrompt()) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No deferred prompt available');
      return false;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
        setIsInstalled(true);
      } else {
        console.log('[PWA] User dismissed the install prompt');
        dismissInstallPrompt(true);
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
      return outcome === 'accepted';
    } catch (error) {
      console.error('[PWA] Install error:', error);
      return false;
    }
  }, [deferredPrompt]);

  const dismiss = useCallback((remindLater = true) => {
    dismissInstallPrompt(remindLater);
    setShowPrompt(false);
  }, []);

  const message = getInstallMessage(userRole);

  return {
    showPrompt,
    isIOS,
    isInstalled,
    canInstall: !!deferredPrompt,
    installApp,
    dismiss,
    message
  };
}

export default useInstallPrompt;
